import type { OrderDetails } from '../model'

/**
 * Gets the field value from hsFields by matching the key with a case-insensitive regex (explicit/full match).
 */
export function getFieldValueByRegex(
  hsFields: OrderDetails['hsFields'] | undefined,
  regex: RegExp,
): string | undefined {
  if (!hsFields)
    return undefined
  for (const key of Object.keys(hsFields)) {
    if (typeof key === 'string' && regex.test(key) && hsFields[key]) {
      // Only return if the key matches the regex exactly (full match)
      if (key.match(regex)?.[0] === key) {
        return hsFields[key]
      }
    }
  }
  return undefined
}

/**
 * Gets the field value from hsFields based on a list of possible keys.
 * Useful for retrieving values that may have different naming conventions.
 */
export function getFieldValue(
  hsFields: OrderDetails['hsFields'] | undefined,
  possibleKeys: string[],
): string | undefined {
  if (!hsFields)
    return undefined
  for (const key of possibleKeys) {
    if (hsFields[key])
      return hsFields[key]
  }
  return undefined
}

function getVanSeqValue(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(van seq)$/i)
}

/**
 * Extracts van and sequence numbers from "Van Seq" field (format: "Van 15 Seq 1")
 * Returns { vanNumber, sequenceNumber } or undefineds if not found
 */
export function getVanSeq(hsFields: OrderDetails['hsFields']): {
  vanNumber?: string
  sequenceNumber?: string
} {
  const value = getVanSeqValue(hsFields)

  if (!value || typeof value !== 'string') {
    return {}
  }

  // Match "Van <num> Seq <num>" (flexible spacing)
  const match = value.match(/Van\s*(\d+)\s*Seq\s*(\d+)/i)

  if (match) {
    return {
      vanNumber: match[1],
      sequenceNumber: match[2],
    }
  }

  return {}
}

/**
 * @name getCollectionWindow
 */
export function getCollectionWindow(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(collection window)$/i)
}

/**
 * @name getCustomerName
 */
export function getCustomerName(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(customer name)$/i)
}

/**
 * @name getDriverName
 */
export function getDriverName(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(driver name|drivername)$/i) || hsFields?.driverName
}

/**
 * @name getDriverPhone
 */
export function getDriverPhone(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(driver phone)$/i)
}

/**
 * @name getPartnerPickupTime
 */
export function getPartnerPickupTime(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(partner pickup time|partnerPickupTime)$/i)
}

/**
 * @name getOrderType
 */
export function getOrderType(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(order type)$/i)
}

/**
 * @name getRestricted
 */
export function getRestricted(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(restricted)$/i)
}

/**
 * @name getOrderId
 */
export function getOrderId(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(orderid)$/i)
}

/**
 * @name getEmoji
 */
export function getEmoji(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(emojis)$/i)
}

/**
 * @name getCrateAmbient
 */
export function getCrateAmbient(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(ambient)$/i)
}

/**
 * @name getCrateBulkAmbient
 */
export function getCrateBulkAmbient(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(bulk ambient)$/i)
}

/**
 * @name getCrateBulkChilled
 */
export function getCrateBulkChilled(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(bulk chilled)$/i)
}

/**
 * @name getCrateChilled
 */
export function getCrateChilled(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(chilled)$/i)
}

/**
 * @name getCrateFrozen
 */
export function getCrateFrozen(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(frozen)$/i)
}

/**
 * @name getCrateHotFood
 */
export function getCrateHotFood(hsFields: OrderDetails['hsFields']): string | undefined {
  return getFieldValueByRegex(hsFields, /^(hot food)$/i)
}
